/**
 * A param's response curve: a control's 0–1 position ↔ the param's value, over `minimum`…`maximum`. The slider and every binding read the same pair, so one position is one value on both. Node-safe.
 */

const clamp01 = x => (x < 0 ? 0 : x > 1 ? 1 : x)

const linear = (lo, hi) => [x => lo + (hi - lo) * x, v => (v - lo) / (hi - lo)]

const CURVES = {
	// `M·t³`, t over the range's cube roots: a bipolar range is still at its centre, and ±8 reads ×1 at ¾ throw.
	cubic: (lo, hi) => {
		const m = Math.max(Math.abs(lo), Math.abs(hi))
		const a = Math.cbrt(lo / m)
		const b = Math.cbrt(hi / m)
		return [x => m * (a + (b - a) * x) ** 3, v => (Math.cbrt(v / m) - a) / (b - a)]
	},
	// Geometric: equal throw, equal ratio. Needs a positive range.
	log: (lo, hi) => (lo > 0 ? [x => lo * (hi / lo) ** x, v => Math.log(v / lo) / Math.log(hi / lo)] : linear(lo, hi)),
	// More travel at the small end.
	power: (lo, hi) => [x => lo + (hi - lo) * x * x, v => Math.sqrt(Math.max(0, (v - lo) / (hi - lo)))],
}

const cache = new WeakMap()

/** `[toValue, toPosition]` for a numeric schema with a finite range, or null when it is linear or has none. */
export const paramCurve = schema => {
	const make = CURVES[schema?.curve]
	if (
		!make ||
		!Number.isFinite(schema.minimum) ||
		!Number.isFinite(schema.maximum) ||
		schema.maximum <= schema.minimum
	)
		return null
	let pair = cache.get(schema)
	if (!pair) {
		const [toValue, toPosition] = make(schema.minimum, schema.maximum)
		pair = [x => toValue(clamp01(x)), v => clamp01(toPosition(Number(v)) || 0)]
		cache.set(schema, pair)
	}
	return pair
}

/** A value's 0–1 position on its control. */
export const positionOf = (schema, value) => {
	const c = paramCurve(schema)
	if (c) return c[1](value)
	const span = schema.maximum - schema.minimum
	return span ? clamp01((Number(value) - schema.minimum) / span) : 0
}

/** The value at a 0–1 position, unsnapped. */
export const valueAt = (schema, x) => {
	const c = paramCurve(schema)
	return c ? c[0](x) : schema.minimum + (schema.maximum - schema.minimum) * clamp01(x)
}
